import { useEffect, useRef, useState } from 'react';
import { useChatStore } from '../../stores/chat';
import { useAuthStore } from '../../stores/auth';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import ChatHeader from './ChatHeader';
import ThreadModeStrip from './ThreadModeStrip';
import TeammatePicker from './TeammatePicker';
import { isEffectivelySigned } from '../../lib/signing';
import { useMobile } from '../../hooks/useMobile';
import FilterTabs from '../ui/FilterTabs';
import { format } from 'date-fns';

type MsgFilter = 'all' | 'client' | 'internal';

interface Props { onBack?: () => void; }

export default function ChatPanel({ onBack }: Props = {}) {
  const activeThreadId = useChatStore((s) => s.activeThreadId);
  const threads = useChatStore((s) => s.threads);
  const messages = useChatStore((s) => s.messages);
  const loadingMessages = useChatStore((s) => s.loadingMessages);
  const typingUsers = useChatStore((s) => s.typingUsers);
  const leftThreads = useChatStore((s) => s.leftThreads);
  const workspaceSignDefault = useChatStore((s) => s.workspaceSignDefault);
  const user = useAuthStore((s) => s.user);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Sticky per thread (UI-05): once set here, note mode for a given thread
  // persists across switches — deliberately not reset when activeThreadId changes.
  const [noteModeByThread, setNoteModeByThread] = useState<Record<string, boolean>>({});
  const [headerPickerOpen, setHeaderPickerOpen] = useState(false);
  const [msgFilter, setMsgFilter] = useState<MsgFilter>('all');
  const isMobile = useMobile();

  const activeThread = threads.find((t) => t.id === activeThreadId);
  const threadMessages = activeThreadId ? (messages[activeThreadId] ?? []) : [];
  const typingInThread = activeThreadId ? (typingUsers[activeThreadId] ?? []) : [];
  const leftInfo = activeThreadId ? leftThreads[activeThreadId] : undefined;
  const hasLeft = !!leftInfo;

  const isNoteMode = activeThreadId ? (noteModeByThread[activeThreadId] ?? false) : false;
  // Amber ("hidden from a present client") applies only to client threads in
  // note mode — team-only threads (internal/direct/org) have no client to hide
  // from and use the blue "team only" strip + normal chat background instead.
  const showNotesBg = isNoteMode && activeThread?.type === 'client';
  const signed = activeThread ? isEffectivelySigned(activeThread, workspaceSignDefault) : false;
  const signedLabel = signed ? `SIGNED AS ${(user?.name ?? 'YOU').toUpperCase()}` : 'NOT SIGNED';

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadMessages.length]);

  if (!activeThreadId) {
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'var(--surface-chat)', color: 'var(--shell-xs)', gap: 12,
      }}>
        {onBack && (
          <button onClick={onBack} style={{ position: 'absolute', top: 12, left: 12, background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '0.83rem' }}>← Back</button>
        )}
        <div style={{ fontSize: '2.5rem', opacity: 0.25 }}>💬</div>
        <p style={{ fontSize: '0.88rem' }}>Select a thread to start messaging</p>
      </div>
    );
  }

  if (!activeThread && !loadingMessages) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--surface-chat)', color: 'var(--shell-xs)', fontSize: '0.88rem',
      }}>
        Loading conversation…
      </div>
    );
  }

  // Desktop-only filter tabs (3e) — client-side over already-fetched messages, no new endpoint.
  const showFilterTabs = !isMobile && activeThread?.type === 'client';
  const internalCount = threadMessages.filter((m) => m.is_internal).length;
  const filteredMessages = !showFilterTabs || msgFilter === 'all'
    ? threadMessages
    : threadMessages.filter((m) => m.is_system || (msgFilter === 'internal' ? m.is_internal : !m.is_internal));

  // Group messages by date
  const grouped: Array<{ date: string; messages: typeof threadMessages }> = [];
  for (const msg of filteredMessages) {
    const date = format(new Date(msg.created_at), 'MMMM d, yyyy');
    const last = grouped[grouped.length - 1];
    if (last?.date === date) last.messages.push(msg);
    else grouped.push({ date, messages: [msg] });
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ChatHeader thread={activeThread ?? null} onBack={onBack} onOpenAddTeammate={() => setHeaderPickerOpen(true)} />
      {activeThread && showFilterTabs ? (
        <div style={{ padding: '6px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)' }}>
          <FilterTabs
            value={msgFilter}
            onChange={setMsgFilter}
            tabs={[
              { value: 'all', label: 'Everything' },
              { value: 'client', label: 'Client-visible' },
              { value: 'internal', label: 'Internal only', count: internalCount, tone: 'var(--note-time)' },
            ]}
          />
          <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', color: isNoteMode ? 'var(--note-label)' : 'var(--v2)' }}>
            {isNoteMode ? "INTERNAL · CLIENT WON'T SEE THIS" : `MESSAGING CLIENT · ${signedLabel}`}
          </span>
        </div>
      ) : activeThread && (
        <ThreadModeStrip
          thread={activeThread}
          isNoteMode={isNoteMode}
          signedLabel={signedLabel}
          onSwitchBack={() => activeThreadId && setNoteModeByThread((p) => ({ ...p, [activeThreadId]: false }))}
        />
      )}

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '16px 20px',
        display: 'flex', flexDirection: 'column', gap: 8,
        background: showNotesBg ? 'var(--chat-bg-internal)' : 'var(--chat-bg-external)',
        borderTop: showNotesBg ? '2px solid var(--chat-internal-border)' : undefined,
        opacity: hasLeft ? 0.6 : 1,
        transition: 'background .2s, border-color .2s, opacity .2s',
      }}>
        {loadingMessages ? (
          <div style={{ textAlign: 'center', color: 'var(--shell-xs)', padding: 32 }}>Loading…</div>
        ) : (
          grouped.map(({ date, messages: dayMsgs }) => (
            <div key={date} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{
                fontSize: '0.68rem', color: 'var(--shell-xs)', margin: '4px 0 8px',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
                {date}
                <div style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
              </div>
              {dayMsgs.map((msg) => (
                <MessageBubble key={msg.id} message={msg} currentUserId={user?.id ?? ''} threadType={activeThread?.type} notesMode={showNotesBg} signed={signed} />
              ))}
            </div>
          ))
        )}

        {typingInThread.length > 0 && !hasLeft && (
          <div style={{ color: 'var(--shell-xs)', fontSize: '0.75rem', fontStyle: 'italic', paddingLeft: 4 }}>
            {typingInThread.map((u) => u.name).join(', ')} {typingInThread.length === 1 ? 'is' : 'are'} typing…
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Left / Removed banner — replaces input */}
      {hasLeft ? (
        <div style={{
          borderTop: '1px solid var(--shell-border)',
          background: 'var(--vs1)',
          padding: '14px 20px',
          display: 'flex', alignItems: 'center', gap: 10,
          color: 'var(--shell-xs)', fontSize: '0.8rem',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '1rem', opacity: 0.5 }}>🚪</span>
          <span>
            {leftInfo.removedBy
              ? <><strong style={{ color: 'var(--shell-muted)' }}>{leftInfo.removedBy}</strong> removed you from this relay</>
              : <>You have left this relay</>
            }
          </span>
        </div>
      ) : (
        <MessageInput
          threadId={activeThreadId}
          isInternal={isNoteMode}
          onIsInternalChange={(v) => setNoteModeByThread((p) => ({ ...p, [activeThreadId]: v }))}
        />
      )}

      {headerPickerOpen && activeThreadId && (
        <TeammatePicker threadId={activeThreadId} mode="add" onClose={() => setHeaderPickerOpen(false)} />
      )}
    </div>
  );
}
